import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Eye, Check, Plus } from 'lucide-react';

const SchemaSelector = ({ 
  schemas = [], 
  selectedSchemaId, 
  onSelect, 
  onPreview,
  onCreateNew,
  className = "" 
}) => {
  const [previewSchema, setPreviewSchema] = useState(null);

  const handleSelect = (schemaId) => {
    if (onSelect) {
      onSelect(schemaId);
    }
  };

  const handlePreview = (schema) => {
    setPreviewSchema(schema);
    if (onPreview) {
      onPreview(schema);
    }
  };

  const closePreview = () => {
    setPreviewSchema(null);
  };

  const renderFieldPreview = (field) => {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        {field.type === 'text' && (
          <div className="h-10 bg-gray-100 border border-gray-300 rounded-md flex items-center px-3">
            <span className="text-gray-500 text-sm">{field.placeholder || 'Text input'}</span>
          </div>
        )}
        
        {field.type === 'email' && (
          <div className="h-10 bg-gray-100 border border-gray-300 rounded-md flex items-center px-3">
            <span className="text-gray-500 text-sm">{field.placeholder || 'Email input'}</span>
          </div>
        )}
        
        {field.type === 'number' && (
          <div className="h-10 bg-gray-100 border border-gray-300 rounded-md flex items-center px-3">
            <span className="text-gray-500 text-sm">{field.placeholder || 'Number input'}</span>
          </div>
        )}
        
        {field.type === 'date' && (
          <div className="h-10 bg-gray-100 border border-gray-300 rounded-md flex items-center px-3">
            <span className="text-gray-500 text-sm">Date picker</span>
          </div>
        )}
        
        {field.type === 'textarea' && (
          <div className="h-20 bg-gray-100 border border-gray-300 rounded-md flex items-center px-3">
            <span className="text-gray-500 text-sm">{field.placeholder || 'Text area'}</span>
          </div>
        )}
        
        {field.type === 'dropdown' && (
          <div className="h-10 bg-gray-100 border border-gray-300 rounded-md flex items-center px-3">
            <span className="text-gray-500 text-sm">
              {field.options?.length ? `${field.options.length} options` : 'Dropdown'}
            </span>
          </div>
        )}
        
        {field.type === 'file' && (
          <div className="h-16 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center">
            <span className="text-gray-500 text-sm">File upload</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Schema Selection */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Select Form Schema</CardTitle>
            {onCreateNew && (
              <Button variant="outline" onClick={onCreateNew}>
                <Plus className="w-4 h-4 mr-2" />
                Create New
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {schemas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No form schemas available.</p>
              {onCreateNew && (
                <Button className="mt-4" onClick={onCreateNew}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Schema
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {schemas.map((schema) => (
                <div
                  key={schema.schemaId}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedSchemaId === schema.schemaId
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleSelect(schema.schemaId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900">{schema.name}</h3>
                        {selectedSchemaId === schema.schemaId && (
                          <Check className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {schema.fields?.length || 0} fields
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {schema.fields?.slice(0, 3).map((field, index) => (
                          <span
                            key={index}
                            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                          >
                            {field.type}
                          </span>
                        ))}
                        {schema.fields?.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{schema.fields.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreview(schema);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Modal */}
      {previewSchema && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">Preview: {previewSchema.name}</CardTitle>
                <Button variant="ghost" onClick={closePreview}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {previewSchema.fields?.map((field, index) => (
                  <div key={index}>
                    {renderFieldPreview(field)}
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t">
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Total fields: {previewSchema.fields?.length || 0}</span>
                  <span>
                    Required fields: {previewSchema.fields?.filter(f => f.required).length || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SchemaSelector;
